'use client';

import dynamic from 'next/dynamic';
import { useState } from 'react';
import { BreadcrumbEllipsis } from '@/app/_components/core/breadcrumb';
import { SmartLink } from '@/app/_components/core/smart-link';
import type { BreadcrumbItem as BreadcrumbItemType } from '@/app/_components/shared/breadcrumb/breadcrumb.types';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/app/_components/ui/dropdown-menu';
import { useLabel } from '@/app/_contexts/labels-context';
import { useMediaQuery } from '@/app/_hooks/use-media-query';

interface BreadcrumbOverflowMenuProps {
  items: BreadcrumbItemType[];
  trackingPrefix?: string;
}

// Mobile Drawer variant lazy-loaded — vaul stays out of the desktop bundle
// (and out of any breadcrumb that doesn't overflow on mobile).
const BreadcrumbOverflowDrawer = dynamic(
  () => import('./breadcrumb-overflow-drawer'),
  { ssr: false },
);

export function BreadcrumbOverflowMenu({ items, trackingPrefix }: BreadcrumbOverflowMenuProps) {
  const [open, setOpen] = useState(false);
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const navigateToLabel = useLabel('navigateTo', 'Navigate to');
  const closeLabel = useLabel('close', 'Close');
  const toggleMenuLabel = useLabel('toggleMenu', 'Toggle menu');

  if (isDesktop) {
    return (
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger
          className="flex items-center gap-1"
          aria-label={toggleMenuLabel}
          data-track-id={trackingPrefix ? `${trackingPrefix}-overflow-trigger` : undefined}
        >
          <BreadcrumbEllipsis className="[&>svg]:size-4" />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="bg-popover text-popover-foreground rounded-md border border-border"
          align="start"
        >
          {items.map((item, index) => (
            <DropdownMenuItem
              className="rounded-md text-body-sm/normal  p-0"
              key={item.href}
            >
              <SmartLink
                href={item.href}
                className="w-full px-2 py-1.5"
                data-track-id={
                  trackingPrefix
                    ? `${trackingPrefix}-overflow-item-${index + 1}`
                    : undefined
                }
              >
                {item.title}
              </SmartLink>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <BreadcrumbOverflowDrawer
      items={items}
      open={open}
      onOpenChange={setOpen}
      navigateToLabel={navigateToLabel}
      closeLabel={closeLabel}
      toggleMenuLabel={toggleMenuLabel}
      trackingPrefix={trackingPrefix}
    />
  );
}
